import { useCallback, useEffect, useRef } from 'react';
import { uchatApi } from '../../../../services/api';

const SDK_MESSAGE_TYPE = 'UCHAT_MARK_READ';

function notifySdkParent(unreadCount: number) {
  if (window.parent === window) {
    return;
  }
  window.parent.postMessage({ type: SDK_MESSAGE_TYPE, unreadCount }, '*');
}

/**
 * 仅在用户进入可视聊天区时标记已读（IntersectionObserver + 页面可见）。
 * 不在每条新消息到达时 mark-read，避免发图自动回复刚 +1 就被当场清零。
 */
export function useMarkReadWhenViewed(options: {
  code?: string;
  uid?: string;
  csUuid?: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  enabled: boolean;
  messageCount: number;
}) {
  const { code, uid, csUuid, containerRef, enabled, messageCount } = options;
  const isViewingRef = useRef(false);
  const prevMessageCountRef = useRef(0);
  const optsRef = useRef({ code, uid, csUuid, enabled, messageCount });
  optsRef.current = { code, uid, csUuid, enabled, messageCount };

  const markReadIfViewing = useCallback(async () => {
    const opts = optsRef.current;
    if (!opts.enabled || !opts.code || !opts.uid || !opts.csUuid || opts.messageCount <= 0) {
      return;
    }
    if (document.visibilityState !== 'visible' || !isViewingRef.current) {
      return;
    }

    try {
      const res = await uchatApi.markRead({
        code: opts.code,
        uid: opts.uid,
        csUuid: opts.csUuid,
      });
      if (res?.success) {
        notifySdkParent(res.data?.unreadCount ?? 0);
      }
    } catch {
      // 已读标记失败不影响聊天
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        isViewingRef.current =
          Boolean(entry?.isIntersecting) && (entry?.intersectionRatio ?? 0) >= 0.25;
        if (isViewingRef.current) {
          void markReadIfViewing();
        }
      },
      { threshold: [0, 0.25, 0.5] },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef, enabled, markReadIfViewing]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void markReadIfViewing();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [markReadIfViewing]);

  // 仅首批消息落地补一次：打开会话时 Intersection 可能早于有消息，避免历史未读残留
  useEffect(() => {
    const prev = prevMessageCountRef.current;
    prevMessageCountRef.current = messageCount;
    if (prev <= 0 && messageCount > 0) {
      void markReadIfViewing();
    }
  }, [messageCount, markReadIfViewing]);
}
